import { FC, Fragment, useEffect, useMemo, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { useDirection } from '@hooks';
import { AssessmentDomainStatus, ProgressFlowStepType } from '@libs/constants';
import { SingularKeys, Trans } from '@libs/i18n';
import { NoFlipBox } from '@utils';

import { ProgressFlowCircle, ProgressFlowCircleName } from './ProgressFlowCircle';
import { ProgressFlowCongestedBlock } from './ProgressFlowCongestedBlock';

/**
 * Represents a single item in the progress flow
 */
export interface ProgressFlowItem {
  id: string;
  title: string;
  isOptional?: boolean;
}

// Re-export for backward compatibility
export type DomainState = AssessmentDomainStatus;

/**
 * Represents a single step displayed as an individual circle in the flow
 */
interface SingleStep {
  type: ProgressFlowStepType.SINGLE;
  status: AssessmentDomainStatus;
  title: string;
  id: string;
  index: number;
  isOptional?: boolean;
}

/**
 * Represents multiple steps collapsed into a congested block
 */
interface MultiStep {
  type: ProgressFlowStepType.MULTI;
  status: AssessmentDomainStatus;
  children: SingleStep[];
  blockId: string;
  startIndex: number;
}

/**
 * Union type for all possible step types in the progress flow
 */
type ProcessedStep = SingleStep | MultiStep;

/**
 * Props for the ProgressFlow component
 */
interface ProgressFlowProps {
  items: ProgressFlowItem[];
  domainStates: Map<number, AssessmentDomainStatus>;
  domainOptionalMap?: Map<number, boolean>;
  /**
   * Per-domain `isModifiedFromPrevious` flag, keyed by 1-based orderIndex.
   * When true the circle gets a small purple "edited" pin overlay so users
   * can see at a glance which domains they've already touched in a
   * re-assessment (version > 1). Pass an empty/undefined map for v1 flows.
   */
  domainModifiedMap?: Map<number, boolean>;
  currentDomainIndex: number;
  totalDomains: number;
  completionPercentage: number;
  isNavigationDisabled?: boolean;
  onNodeClick?: (index: number) => void;
}

// ============================================================================
// CONSTANTS
// ============================================================================

/**
 * Maximum number of steps before congestion logic kicks in
 * If there are 7 or fewer domains, show them all individually
 */
const MAX_SINGLE_STEPS = 7;

/**
 * Number of individual steps visible at the start and end of the flow
 * Used in standard layout: 3 at start + congested block + 3 at end
 */
const VISIBLE_SINGLE_COUNT = 3;

/**
 * Index where congestion starts in the standard layout (0-based)
 * After the first 3 items, remaining items get congested
 */
const CONGESTED_START_INDEX = 3;

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Retrieves the status of a domain at a given index
 * Returns NOT_STARTED if the domain state is not found
 */
const getStatus = (
  index: number,
  domainStates: Map<number, AssessmentDomainStatus>,
): AssessmentDomainStatus => {
  const domainState = domainStates.get(index);
  return domainState ?? AssessmentDomainStatus.NOT_STARTED;
};

/**
 * Creates a single step object from a ProgressFlowItem
 * This represents an individual domain circle in the flow
 */
const createSingleStep = (
  item: ProgressFlowItem,
  index: number,
  domainStates: Map<number, AssessmentDomainStatus>,
): SingleStep => ({
  type: ProgressFlowStepType.SINGLE,
  status: getStatus(index, domainStates),
  title: item.title,
  id: item.id,
  index,
  isOptional: item.isOptional,
});

/**
 * Creates a multi-step (congested block) from multiple items
 * The block status is determined by its children's statuses:
 * - COMPLETED if all children are completed
 * - IN_PROGRESS if any child is in progress OR current domain is within this block
 * - NOT_STARTED otherwise
 */
const createMultiStep = (
  items: ProgressFlowItem[],
  startIndex: number,
  currentDomainIndex: number,
  domainStates: Map<number, AssessmentDomainStatus>,
  blockId: string,
): MultiStep => {
  // Create children first to determine their statuses
  const children = items.map((item, idx) => createSingleStep(item, startIndex + idx, domainStates));

  // Analyze children statuses to determine the parent block status
  const childStatuses = children.map((child) => child.status);
  const allCompleted = childStatuses.every((s) => s === AssessmentDomainStatus.COMPLETED);
  const hasInProgress = childStatuses.some((s) => s === AssessmentDomainStatus.IN_PROGRESS);

  // Check if the current domain index falls within this block's range
  const endIndex = startIndex + items.length;
  const isCurrentInBlock = currentDomainIndex >= startIndex && currentDomainIndex <= endIndex;

  // Determine block status based on children and current position
  const status: AssessmentDomainStatus = allCompleted
    ? AssessmentDomainStatus.COMPLETED
    : hasInProgress || isCurrentInBlock
      ? AssessmentDomainStatus.IN_PROGRESS
      : AssessmentDomainStatus.NOT_STARTED;

  return {
    type: ProgressFlowStepType.MULTI,
    status,
    blockId,
    startIndex,
    children,
  };
};

/**
 * Converts an array of ProgressFlowItems into SingleStep objects
 * Used for creating sequences of individual domain circles
 */
const createSingleSteps = (
  items: ProgressFlowItem[],
  startIndex: number,
  domainStates: Map<number, AssessmentDomainStatus>,
): SingleStep[] => {
  return items.map((item, idx) => createSingleStep(item, startIndex + idx, domainStates));
};

/**
 * Creates the standard layout when user is at the beginning or end of the flow
 * Layout: [3 single] + [congested middle] + [3 single]
 * This is used when currentDomainIndex < 3 OR after the middle congested area
 */
const createStandardLayout = (
  items: ProgressFlowItem[],
  currentDomainIndex: number,
  domainStates: Map<number, AssessmentDomainStatus>,
): ProcessedStep[] => {
  const totalItems = items.length;
  const steps: ProcessedStep[] = [];

  // First three items as individual steps (array indices 0-2)
  const firstThree = items.slice(0, VISIBLE_SINGLE_COUNT);

  // Middle items that will be congested (array indices 3 to totalItems-4)
  const middleItems = items.slice(
    CONGESTED_START_INDEX,
    Math.max(CONGESTED_START_INDEX, totalItems - VISIBLE_SINGLE_COUNT),
  );

  // Last three items as individual steps (if there are more than 6 items total)
  const lastThree = totalItems > 6 ? items.slice(totalItems - VISIBLE_SINGLE_COUNT) : [];

  // Add first three as SINGLE steps - orderIndex starts at 1 (1-based indexing)
  steps.push(...createSingleSteps(firstThree, 1, domainStates));

  // Add middle items as a MULTI (congested) block
  if (middleItems.length > 0) {
    steps.push(
      createMultiStep(
        middleItems,
        CONGESTED_START_INDEX + 1, // Convert to 1-based orderIndex
        currentDomainIndex,
        domainStates,
        'middle-block',
      ),
    );
  }

  // Add last three as SINGLE steps
  if (lastThree.length > 0) {
    const lastStartIndex = CONGESTED_START_INDEX + middleItems.length + 1; // 1-based
    steps.push(...createSingleSteps(lastThree, lastStartIndex, domainStates));
  }

  return steps;
};

/**
 * Main transformation function that converts raw items into processed steps
 * Handles three scenarios:
 * 1. Simple: 7 or fewer items - show all as individual circles
 * 2. Standard: Current domain is near start/end - use standard layout
 * 3. Dynamic: Current domain is in middle - show context around current position
 * 
 * Note: Domain indices are 1-based (orderIndex), array indices are 0-based
 */
const transformToProcessedSteps = (
  items: ProgressFlowItem[],
  domainStates: Map<number, AssessmentDomainStatus>,
  currentDomainIndex: number,
): ProcessedStep[] => {

  const totalItems = items.length;


  // ========== SCENARIO 1: Simple Flow (7 or fewer items) ==========
  // Show all items as individual circles - no congestion needed
  if (totalItems <= MAX_SINGLE_STEPS) {
    return createSingleSteps(items, 1, domainStates);
  }

  // ========== SCENARIO 2: Standard Layout (Current near start) ==========
  // When currentDomainIndex < 3 (before 3rd item)
  // Layout: [3 single] + [congested middle] + [3 single]
  if (currentDomainIndex < 3) {
    return createStandardLayout(items, currentDomainIndex, domainStates);
  }

  // Calculate the end of the congested middle area
  const congestedEnd = Math.max(CONGESTED_START_INDEX + 1, totalItems - 3);

  // ========== SCENARIO 3: Dynamic Layout (Current in middle) ==========
  // When currentDomainIndex is between 3 and (totalItems - 3)
  // Layout: [completed before] + [left congested] + [current + next] + [right congested] + [last items]
  if (currentDomainIndex >= CONGESTED_START_INDEX + 1 && currentDomainIndex <= congestedEnd) {
    const steps: ProcessedStep[] = [];

    // --- Completed before section ---
    // Show first 2 completed items individually (array indices 0-1, orderIndex 1-2)
    const completedBefore = items.slice(0, Math.min(2, currentDomainIndex - 1));
    if (completedBefore.length > 0) {
      steps.push(...createSingleSteps(completedBefore, 1, domainStates));
    }

    // --- Left congested section ---
    // Completed items in the middle (between completed before and current)
    const leftCongestedArrayStart = 2; // Array index 2 (orderIndex 3)
    const leftCongestedArrayEnd = currentDomainIndex - 2; // Convert 1-based to 0-based array index

    if (leftCongestedArrayEnd >= leftCongestedArrayStart) {
      const leftCongested = items.slice(leftCongestedArrayStart, leftCongestedArrayEnd + 1);
      steps.push(
        createMultiStep(
          leftCongested,
          leftCongestedArrayStart + 1, // Convert to 1-based orderIndex (starts at 3)
          currentDomainIndex,
          domainStates,
          'left-block',
        ),
      );
    }

    // --- Current and next section ---
    // Show current domain and next domain as individual circles
    const currentArrayIndex = currentDomainIndex - 1; // Convert 1-based to 0-based
    const currentAndNext = items.slice(
      currentArrayIndex,
      Math.min(currentArrayIndex + 2, totalItems),
    );
    if (currentAndNext.length > 0) {
      steps.push(...createSingleSteps(currentAndNext, currentDomainIndex, domainStates));
    }

    // --- Right congested section ---
    // Future items after current+next, before the last 3
    const rightCongestedArrayStart = currentArrayIndex + 2;
    const rightCongestedArrayEnd = Math.max(
      rightCongestedArrayStart,
      totalItems - VISIBLE_SINGLE_COUNT,
    );

    if (rightCongestedArrayEnd >= rightCongestedArrayStart) {
      const rightCongested = items.slice(rightCongestedArrayStart, rightCongestedArrayEnd + 1);
      steps.push(
        createMultiStep(
          rightCongested,
          rightCongestedArrayStart + 1, // Convert to 1-based orderIndex
          currentDomainIndex,
          domainStates,
          'right-block',
        ),
      );
    }

    // --- Last items section ---
    // Show last 3 items individually (if any remain after right congested)
    const lastItems = totalItems > rightCongestedArrayEnd + 1 ? items.slice(rightCongestedArrayEnd + 1) : [];
    if (lastItems.length > 0) {
      steps.push(...createSingleSteps(lastItems, rightCongestedArrayEnd + 2, domainStates)); // +2 to convert to 1-based
    }

    return steps;
  }

  return createStandardLayout(items, currentDomainIndex, domainStates);
};

// ============================================================================
// COLOR CONSTANTS
// ============================================================================

/**
 * Color mapping for connecting lines between steps based on their status
 */
const LINE_COLORS = {
  completed: 'green.700', // Green for completed sections
  current: 'blue.700', // Blue for current/in-progress sections
  default: 'grey.400', // Grey for not-started sections
} as const;

// ============================================================================
// MAIN COMPONENT
// ============================================================================

/**
 * ProgressFlow Component
 * 
 * Displays an adaptive progress flow visualization for assessment domains.
 * Automatically handles congestion by collapsing middle sections into expandable blocks
 * when there are more than 7 domains.
 * 
 * Features:
 * - Adaptive layout based on number of domains and current position
 * - RTL support
 * - Status-based coloring
 * - Interactive domain selection
 * - Progress percentage display
 */
export const ProgressFlow: FC<ProgressFlowProps> = observer(
  ({
    items,
    domainStates,
    domainOptionalMap,
    domainModifiedMap,
    currentDomainIndex,
    totalDomains,
    completionPercentage,
    isNavigationDisabled,
    onNodeClick,
  }) => {

    // ========== STATE ==========
    // Track which congested block (if any) is currently expanded
    const [expandedBlock, setExpandedBlock] = useState<string | null>(null);

    const { dir } = useDirection();

    // Use selectedIndex if provided, otherwise fall back to currentDomainIndex
    // This allows external control of which domain appears selected
    const activeSelectedIndex = currentDomainIndex;

    // ========== PROCESSED STEPS ==========
    // Transform raw items into display-ready steps (memoized for performance)
    const processedSteps = useMemo(() => {
      const steps = transformToProcessedSteps(items, domainStates, currentDomainIndex);
      // setIsLoading(false);
      return steps;
    }, [items, domainStates, currentDomainIndex]);

    // ========== EFFECT: RESET EXPANDED BLOCK ==========
    // Reset expanded block when current index changes if the block no longer exists
    // Prevents stale state when navigation causes layout restructuring
    useEffect(() => {
      if (expandedBlock) {
        const blockExists = processedSteps.some(
          (step) => step.type === ProgressFlowStepType.MULTI && step.blockId === expandedBlock,
        );
        if (!blockExists) {
          setExpandedBlock(null);
        }
      }
    }, [processedSteps, expandedBlock]);

    // ========== RENDER ==========
    return (
      <NoFlipBox
        mode={dir}
        sx={{
          width: '100%',
          py: 0,
          px: 0,
        }}
      >
        {/* ========== PARENT CONTAINER ========== */}
        {/* Column layout: Header section + Flow visualization */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: { md: 1 } }}>

          {/* ========== HEADER SECTION ========== */}
          {/* Top row showing current domain number and overall progress */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              px: 0,
              flexWrap: { xs: 'wrap', sm: 'nowrap' },
              gap: { xs: 1, sm: 0 },
            }}
          >
            {/* --- Domain Number Label (Left side) --- */}
            <Typography
              variant="textMD_medium"
              sx={{
                color: 'grey.250',
              }}
            >
              <Trans
                i18nKey={'assessments.new.progressFlow.domainNumber' as SingularKeys}
                values={{ number: activeSelectedIndex, total: totalDomains }}
              />
            </Typography>

            {/* --- Progress Percentage Display (Right side) --- */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end',
                gap: 0.5,
              }}
            >
              {/* Progress label */}
              <Typography
                // variant="textXS_medium"
                sx={{
                  color: 'black.200',
                }}
              >
                <Trans i18nKey={'assessments.progressFlow.progress' as SingularKeys} />
              </Typography>

              {/* Percentage value */}
              <Typography
                variant="textSM_regular"
                sx={{
                  color: 'grey.250',
                  fontWeight: 400,
                }}
              >
                {completionPercentage}%
                <Trans i18nKey={'assessments.new.progressFlow.completed' as SingularKeys} />
              </Typography>
            </Box>
          </Box>

          {/* ========== FLOW VISUALIZATION SECTION ========== */}
          {/* Two-row layout: Top row (circles + lines), Bottom row (domain names) */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>

            {/* ========== TOP ROW: CIRCLES AND CONNECTING LINES ========== */}
            <Stack
              direction="row"
              alignItems="center"
              gap={0}
              sx={{
                width: '100%',
              }}
            >
              {processedSteps.map((step, stepIdx) => {
                // Generate unique key for each step
                const stepKey =
                  step.type === ProgressFlowStepType.SINGLE
                    ? `single-${step.id}-${step.index}`
                    : `multi-${step.blockId}-${step.startIndex}`;

                // Determine connecting line color based on step status
                const lineColor =
                  step.status === AssessmentDomainStatus.COMPLETED
                    ? LINE_COLORS.completed
                    : step.status === AssessmentDomainStatus.IN_PROGRESS
                      ? LINE_COLORS.current
                      : LINE_COLORS.default;

                const isLast = step.type === ProgressFlowStepType.SINGLE ? step.index === totalDomains : false;

                return (
                  <Fragment key={`circle-${stepKey}`}>
                    {/* --- Container for Circle + Connecting Line --- */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0,
                        flex: isLast ? 0 : 1, // Last item doesn't flex, others grow to fill space
                      }}
                    >
                      {/* --- Circle or Congested Block --- */}
                      {step.type === ProgressFlowStepType.SINGLE ? (
                        // Single domain circle
                        <ProgressFlowCircle
                          item={{ id: step.id, title: step.title }}
                          index={step.index}
                          isCompleted={step.status === AssessmentDomainStatus.COMPLETED}
                          isCurrent={step.status === AssessmentDomainStatus.IN_PROGRESS}
                          isSelected={step.index === activeSelectedIndex}
                          isOptional={step.isOptional}
                          isModifiedFromPrevious={domainModifiedMap?.get(step.index) ?? false}
                          isNavigationDisabled={isNavigationDisabled}
                          onClick={() => onNodeClick?.(step.index)}
                        />
                      ) : (
                        // Congested block (multiple domains collapsed)
                        <ProgressFlowCongestedBlock
                          domains={step.children.map((child) => ({
                            id: child.id,
                            title: child.title,
                            isOptional: domainOptionalMap?.get(child.index) ?? false,
                          }))}
                          startIndex={step.startIndex}
                          isCompleted={step.status === AssessmentDomainStatus.COMPLETED}
                          blockId={step.blockId}
                          domainStates={domainStates}
                          domainOptionalMap={domainOptionalMap}
                          selectedIndex={activeSelectedIndex}
                          currentDomainIndex={currentDomainIndex}
                          isExpanded={expandedBlock === step.blockId}
                          isNavigationDisabled={isNavigationDisabled}
                          onToggle={() =>
                            setExpandedBlock(expandedBlock === step.blockId ? null : step.blockId)
                          }
                          onNodeClick={onNodeClick}
                        />
                      )}

                      {/* --- Connecting Line (shown after each step except the last) --- */}
                      {!isLast && (
                        <Box
                          sx={{
                            height: { xs: 4, sm: 5, md: 6 },
                            borderRadius: 100,
                            flex: 1, // Grows to fill available space
                            minWidth: { xs: 20, sm: 25, md: 30 },
                            backgroundColor: lineColor,
                            mx: 0.25, // Small horizontal margin
                          }}
                        />
                      )}
                    </Box>
                  </Fragment>
                );
              })}
            </Stack>

            {/* ========== BOTTOM ROW: DOMAIN NAMES ========== */}
            {/* Names are positioned directly under their corresponding circles */}
            <Stack
              direction="row"
              alignItems="flex-start"
              gap={0}
              sx={{
                width: '100%',
              }}
            >
              {processedSteps.map((step, stepIdx) => {
                // Generate unique key for each step (matching the circle row)
                const stepKey =
                  step.type === ProgressFlowStepType.SINGLE
                    ? `single-${step.id}-${step.index}`
                    : `multi-${step.blockId}-${step.startIndex}`;

                const isLast = stepIdx === processedSteps.length - 1;

                return (
                  <Fragment key={`name-${stepKey}`}>
                    {/* --- Container for Name + Spacer (matches circle + line structure) --- */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        flex: isLast ? 0 : 1,
                      }}
                    >
                      {/* --- Domain Name (centered under circle) --- */}
                      <Box
                        sx={{
                          width: { xs: 26, sm: 28, md: 30 }, // Same width as circle
                          flexShrink: 0,
                          position: 'relative',
                          overflow: 'visible',
                          display: 'flex',
                          justifyContent: 'center',
                        }}
                      >
                        {step.type === ProgressFlowStepType.SINGLE ? (
                          // Name for single domain
                          <Box
                            sx={{
                              position: 'absolute',
                              left: '50%',
                              transform: 'translateX(-50%)', // Center horizontally
                              display: 'flex',
                              justifyContent: 'center',
                            }}
                          >
                            <ProgressFlowCircleName
                              item={{ id: step.id, title: step.title }}
                              isSelected={step.index === activeSelectedIndex}
                              isCurrent={step.status === AssessmentDomainStatus.IN_PROGRESS}
                              isOptional={step.isOptional}
                            />
                          </Box>
                        ) : (
                          // No name shown for congested blocks
                          <Box sx={{ height: 0 }} />
                        )}
                      </Box>

                      {/* --- Spacer to match line width (except for last item) --- */}
                      {!isLast && <Box sx={{ flex: 1, mx: 0.25 }} />}
                    </Box>
                  </Fragment>
                );
              })}
            </Stack>
          </Box>
        </Box>
      </NoFlipBox>
    );
  },
);
